import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, Smartphone, Star } from "lucide-react";
import { api, euro } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { APP_URL } from "@/lib/config";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const [state, setState] = useState({ phase: "checking" });
  const { clear } = useCart();
  const cleared = useRef(false);

  useEffect(() => {
    if (!sid) return setState({ phase: "error" });
    let tries = 0, timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") {
          if (!cleared.current) { clear(); cleared.current = true; }
          return setState({ phase: "paid", data });
        }
        if (["expired", "failed"].includes(data.payment_status)) return setState({ phase: "error" });
      } catch { /* retry */ }
      if (++tries < 15) timer = setTimeout(poll, 2000);
      else setState({ phase: "timeout" });
    };
    poll();
    return () => clearTimeout(timer);
  }, [sid]); // eslint-disable-line react-hooks/exhaustive-deps

  if (state.phase === "checking")
    return <div className="min-h-[60vh] grid place-items-center text-zinc-400" data-testid="payment-checking"><span className="flex items-center gap-3"><Loader2 className="animate-spin text-red-500" /> Verifica del pagamento in corso…</span></div>;

  if (state.phase !== "paid")
    return (
      <div className="max-w-xl mx-auto px-5 py-24 text-center" data-testid="payment-error">
        <XCircle size={56} className="mx-auto text-red-500" />
        <h1 className="tms-title text-5xl mt-6">Pagamento non confermato</h1>
        <p className="text-zinc-400 mt-4">{state.phase === "timeout" ? "Non riusciamo ancora a confermare il pagamento. Ricarica la pagina tra qualche istante." : "Il pagamento non è andato a buon fine."}</p>
        <Link to="/servizi" className="btn-red mt-8">Torna ai servizi</Link>
      </div>
    );

  const { data } = state;
  return (
    <div className="max-w-3xl mx-auto px-5 py-20" data-testid="payment-success">
      <div className="text-center reveal">
        <CheckCircle2 size={64} className="mx-auto text-red-500" />
        <p className="eyebrow mt-6">Pagamento confermato</p>
        <h1 className="tms-title text-5xl sm:text-6xl mt-2">Grazie, <span className="font-script normal-case text-red-500">ci vediamo in officina</span></h1>
      </div>
      <div className="mt-12 border border-red-900/50 bg-[#121215] p-8 relative overflow-hidden">
        <div className="absolute inset-0 speed-lines opacity-20" />
        <div className="relative">
          <p className="text-xs uppercase tracking-[0.3em] text-zinc-400">Il tuo codice ordine</p>
          <p className="font-display italic font-black text-5xl sm:text-6xl text-white mt-2 tracking-wider" data-testid="order-code">{data.order_code}</p>
          <p className="text-sm text-zinc-400 mt-2">Mostra questo codice in officina. Ti servirà anche per lasciare una recensione. Ti abbiamo inviato una copia via email.</p>
          <div className="mt-8 divide-y divide-zinc-800 border-t border-zinc-800" data-testid="order-items">
            {data.items.map((i) => (
              <div key={i.product_id} className="flex justify-between py-3 text-sm">
                <span>{i.quantity}× {i.name}</span>
                <span className="text-zinc-300">{euro(i.price * i.quantity)}</span>
              </div>
            ))}
            <div className="flex justify-between py-4">
              <span className="uppercase tracking-widest text-xs text-zinc-400">Totale pagato</span>
              <span className="font-display italic font-black text-3xl text-red-500" data-testid="order-total">{euro(data.amount)}</span>
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-wrap gap-4 justify-center mt-10">
        <Link to={`/recensisci?session_id=${sid}`} className="btn-red" data-testid="success-review-button"><Star size={18} /> Lascia una recensione</Link>
        {APP_URL && <a href={APP_URL} className="btn-ghost" data-testid="success-app-button"><Smartphone size={18} /> Apri l'app</a>}
      </div>
    </div>
  );
}
